import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { ResourceDetailsCardElement } from 'components/ResourceCardElements';
import cx from 'classnames';
import FlexBox from 'flexbox-react';
import { stringifyList, roleNotInstallerBased } from '../../../../../utils';
import { isOverdue } from '../../../../../utils/dateUtils.js';
import { can } from '../../../../../services';
import sectionTheme from 'styles/detailsSection.scss';
import helperCardStyle from './helperCard.scss';
import connectUI from 'redux-ui';
import { connect } from 'react-redux';
import { userResendLink } from 'actionCreators';

const DAY_TRANSLATION = {
  1: 'Sunday',
  2: 'Monday',
  3: 'Tuesday',
  4: 'Wednesday',
  5: 'Thursday',
  6: 'Friday',
  7: 'Saturday',
};

const getDaysDisplay = days => (days.length ? days.map(d => DAY_TRANSLATION[d]).join(', ') : 'NOT AVAILABLE');

const CrewListingSection = (props) => {
  const { title, theme, crews, crewsWorkrooms, crewsSurfaceTypes,
          role, updateUI, parseBadgeLink } = props;
  const sectionTitle = cx([theme.sectionTitle, theme.bigTitle]);

  useEffect(() => () => {
    if (props.ui.successMessage) props.updateUI('successMessage', '');
  });

  const resendLink = crew => props.userResendLink(crew.user_id).then(updateUI('successMessage',
    'The confirmation email was resent. This email will expire in 7 days.'));

  return (<section className={theme.section}>
    <p className={sectionTitle}>{title}</p>
    {crews.map((crew, index) => {
      const crewName = crew.name || `Crew ${index}`;
      const crewStatus = `(${(crew.active ? 'Active' : 'Inactive')})`;
      const crewPay = `(${crew.use_installer_pay ? 'Installer' : 'Crew'} Pay Rates)`;
      const crewDeliveryStatus = crew.can_deliver ? 'Can Deliver' : null;
      const dedicatedDelivery = crew.delivery_dedicated ? 'Delivery Dedicated' : null;
      const crewLoginableStatus = crew.loginable ? 'Can Login' : null;
      const crewTitle = `${crewName} ${crewStatus} ${crewPay}`;
      const badgeDate = crew.badge_expiration.toString() || '';
      let isBadgeOverdue = false;
      if (badgeDate) {
        const dateObj = new Date(badgeDate);
        const dateISO = dateObj.toISOString();
        isBadgeOverdue = isOverdue(dateISO);
      }
      const listItemClasses = cx([{
        [theme.inactive]: crew.disabled,
        [theme.sectionListItem]: true,
      }]);
      const zipCodes = (crew.covered_zip_codes || []).length > 0 ?
        crew.covered_zip_codes.sort((a, b) => a - b).join(', ') :
        'N/A';
      const { crew_specific, pending } = crew;
      const formatTo12Hour = (time24) => {
        const [hour, minute] = time24.split(':');
        const period = hour >= 12 ? 'PM' : 'AM';
        const formattedHour = hour % 12 || 12;
        return `${formattedHour}:${minute} ${period}`;
      };
      // TODO: Hiding TWD - Enable no-unused-vars when Dev is up to date
      // eslint-disable-next-line no-unused-vars
      const travelingZipCodes = (crew.traveling_zip_codes || []).length > 0 ?
        crew.traveling_zip_codes.sort((a, b) => a - b).join(', ') : 'N/A';
      return (<div key={`crew-${crew.id}`} className={listItemClasses}>
        <div className={crew.active ? null : theme.inactive}>
          <div className={theme.innerSection}>
            <div className={sectionTheme.cardHeader}>
              <div className={sectionTheme.cardTitle}>{crewTitle}</div>
              {roleNotInstallerBased(role) && <Fragment>
                <div className={crewDeliveryStatus ? sectionTheme.cardDeliveryStatus : null}>
                  {dedicatedDelivery || crewDeliveryStatus}
                </div>
                {crewDeliveryStatus && <div className={sectionTheme.cardDeliveryStatus}>
                  Delivery Priority: {crew.priority}
                </div>}
                <div className={crewLoginableStatus ? sectionTheme.cardDeliveryStatus : null}>
                  {crewLoginableStatus}
                </div>
                <div>
                  {(pending && crew.loginable && can(role, 'resend', 'installer')) ? <FlexBox>
                    <button
                      type="button"
                      className={sectionTheme.userDetailsLink}
                      onClick={() => resendLink(crew)}
                    >Resend activation email</button>
                    <div className={sectionTheme.userDetailsLinkSeparator} />
                  </FlexBox> : null}
                </div>
              </Fragment>}
            </div>
            {roleNotInstallerBased(role) && <div className={sectionTheme.flexboxDiv}>
              <ResourceDetailsCardElement
                size="small"
                title="contact name"
                value={crew.poc_name}
              />
              <ResourceDetailsCardElement
                size="small"
                title="phone number"
                value={crew.phone_number}
              />
              <ResourceDetailsCardElement
                size="small"
                title="email"
                value={crew.email}
              />
            </div>}
            <div className={sectionTheme.flexboxDiv}>
              <ResourceDetailsCardElement
                size="small"
                title="badge number"
                value={crew.badge_number}
              />
              {isBadgeOverdue ? (<ResourceDetailsCardElement
                size="small"
                title="badge expiration"
                className={helperCardStyle.overdue}
              >{badgeDate}</ResourceDetailsCardElement>) : (<ResourceDetailsCardElement
                size="small"
                title="badge expiration"
                value={badgeDate}
              />)}
              <ResourceDetailsCardElement
                size="small"
                title="badge link"
                value={crew.badge_link || 'N/A'}
              >{crew.badge_link ? (<a
                href={parseBadgeLink(crew.badge_link)} target="_blank" rel="noopener noreferrer"
              >{crew.badge_link}</a>) : 'N/A' }</ResourceDetailsCardElement>
            </div>
            {roleNotInstallerBased(role) && <Fragment>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="workrooms"
                  value={stringifyList(crewsWorkrooms[crew.id]) || 'No workrooms'}
                />
                <ResourceDetailsCardElement
                  size="medium"
                  wrap
                  title="zip codes"
                  value={zipCodes}
                />
                <ResourceDetailsCardElement
                  size="medium"
                  wrap
                  title="Traveling Zip Codes"
                  value={travelingZipCodes}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Surface Types"
                  value={stringifyList(crewsSurfaceTypes[crew.id])}
                />
                <ResourceDetailsCardElement
                  size="medium"
                  wrap
                  title="Default Start Time"
                  value={formatTo12Hour(crew.default_start_time)}
                />
              </div>
            </Fragment>}
            {role !== 'installer' && <div className={sectionTheme.flexboxDiv}>
              <ResourceDetailsCardElement
                title="Notes/Comments"
                wrap
                value={crew.comments || 'No comments'}
              />
            </div>}
            <Fragment>
              <div className={sectionTheme.cardTitle}>Suggestion Availabilities</div>
              {/* <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="medium"
                  wrap
                  title="Install Availability"
                  value={getDaysDisplay(crew.install_availability)}
                />
                <ResourceDetailsCardElement
                  size="medium"
                  wrap
                  title="Delivery Availability"
                  value={getDaysDisplay(crew.delivery_availability)}
                />
              </div> */}
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="medium"
                  wrap
                  title={`${crew.delivery_dedicated ? 'Morning' : 'Daily'} Availability`}
                  value={getDaysDisplay(crew.daily_availability.morning)}
                />
                {crew.delivery_dedicated && <ResourceDetailsCardElement
                  size="medium"
                  wrap
                  title="Afternoon Availability"
                  value={getDaysDisplay(crew.daily_availability.afternoon)}
                />}
              </div>
            </Fragment>
            {can(role, 'view', 'crew_specifics') && <Fragment>
              <div className={sectionTheme.cardTitle}>Crew Specifics</div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Max Travel Time (mins)"
                  value={crew_specific.max_travel_time}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Phone Call Needed"
                  value={crew_specific.phone_call_needed ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Hardwood SF Per Day"
                  value={crew_specific.hardwood_sf_per_day}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Carpet SF Per Day"
                  value={crew_specific.carpet_sf_per_day}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Tile SF Per Day"
                  value={crew_specific.tile_sf_per_day}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Laminate SF Per Day"
                  value={crew_specific.laminate_sf_per_day}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Vinyl SF Per Day"
                  value={crew_specific.vinyl_sf_per_day}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Vinyl Plank SF Per Day"
                  value={crew_specific.vinyl_plank_sf_per_day}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Backsplash SF Per Day"
                  value={crew_specific.backsplash_sf_per_day}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Speak English"
                  value={crew_specific.can_speak_english ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Speak Spanish"
                  value={crew_specific.can_speak_spanish ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Move Furniture"
                  value={crew_specific.can_move_furniture ? 'Yes' : 'No'}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Move Oversized Furniture"
                  value={crew_specific.can_move_furniture_oversized ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Move Appliances"
                  value={crew_specific.can_move_appliances ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Subfloor Prep"
                  value={crew_specific.can_do_subfloor_prep ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Subfloor Replacement"
                  value={crew_specific.can_do_subfloor_replacement ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Naildown Hardwood"
                  value={crew_specific.can_do_naildown_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Gluedown Hardwood"
                  value={crew_specific.can_do_gluedown_hardwood ? 'Yes' : 'No'}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Floating Hardwood"
                  value={crew_specific.can_do_floating_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Cali Bamboo"
                  value={crew_specific.can_do_cali_bamboo_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Hardwood Steps"
                  value={crew_specific.can_do_steps_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Stretch In Carpet"
                  value={crew_specific.can_do_stretch_in_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Gluedown Carpet"
                  value={crew_specific.can_do_gluedown_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Binding Carpet"
                  value={crew_specific.can_do_binding_carpet ? 'Yes' : 'No'}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Pattern Match Carpet"
                  value={crew_specific.can_do_pattern_match_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Berber Carpet"
                  value={crew_specific.can_do_berber_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Carpet Steps"
                  value={crew_specific.can_do_steps_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Floor Tile"
                  value={crew_specific.can_do_floor_tile ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Tile Patterns"
                  value={crew_specific.can_do_patterns_tile ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Large Format Tile"
                  value={crew_specific.can_do_large_format_tile ? 'Yes' : 'No'}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Backsplash Tile"
                  value={crew_specific.can_do_backsplash_tile ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Glass Backsplash"
                  value={crew_specific.can_do_backsplash_glass ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Backsplash Blocking"
                  value={crew_specific.can_do_backsplash_blocking ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Sheet Vinyl"
                  value={crew_specific.can_do_vinyl_sheet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Vinyl Tile"
                  value={crew_specific.can_do_vct_lvt ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Gluedown VP"
                  value={crew_specific.can_do_gluedown_vinyl_plank ? 'Yes' : 'No'}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Floating VP"
                  value={crew_specific.can_do_floating_vinyl_plank ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove/Replace Toilet"
                  value={crew_specific.can_remove_replace_toilet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Naildown Hardwood"
                  value={crew_specific.can_remove_naildown_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Floating Hardwood"
                  value={crew_specific.can_remove_floating_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Gluedown Hardwood"
                  value={crew_specific.can_remove_gluedown_hardwood ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Carpet Over Pad"
                  value={crew_specific.can_remove_over_pad_carpet ? 'Yes' : 'No'}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Gluedown Carpet"
                  value={crew_specific.can_remove_gluedown_carpet ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Laminate"
                  value={crew_specific.can_remove_laminate ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Tile"
                  value={crew_specific.can_remove_tile ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Gluedown Vinyl"
                  value={crew_specific.can_remove_gluedown_vinyl ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Vinyl Plank"
                  value={crew_specific.can_remove_vinyl_plank ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Remove Backsplash"
                  value={crew_specific.can_remove_backsplash ? 'Yes' : 'No'}
                />
              </div>
              <div className={sectionTheme.flexboxDiv}>
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Install Baseboard"
                  value={crew_specific.can_install_hard_transitions ? 'Yes' : 'No'}
                />
                <ResourceDetailsCardElement
                  size="small"
                  wrap
                  title="Install Hard Surface Transition"
                  value={crew_specific.can_do_baseboard ? 'Yes' : 'No'}
                />
              </div>
            </Fragment>}
          </div>
        </div>
      </div>);
    })}
  </section>);
};

CrewListingSection.propTypes = {
  title: PropTypes.string,
  crewsWorkrooms: PropTypes.object,
  crewsSurfaceTypes: PropTypes.object,
  theme: PropTypes.object,
  crews: PropTypes.array,
  role: PropTypes.string,
  updateUI: PropTypes.func,
  ui: PropTypes.object,
  userResendLink: PropTypes.func,
  parseBadgeLink: PropTypes.func,
};

export default connectUI()(connect(null, { userResendLink })(CrewListingSection));



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/installers/components/crewListingSection.jsx